import React, { useContext, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Button, Box, Typography, CircularProgress } from "@mui/material";

import Gardens from "./pages/Gardens";
import GardenCrops from "./pages/GardenCrops";
import ManageGardenPlots from "./pages/ManageGardenPlots";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import Crops from "./pages/Crops";
import ManagementPage from "./pages/ManagementPage";
import FieldGuide from "./pages/FieldGuide";
import SeasonalPlanting from "./pages/fieldguide/SeasonalPlanting";
import CropRotation from "./pages/fieldguide/CropRotation";
import SoilManagement from "./pages/fieldguide/SoilManagement";
import PlotManagement from "./pages/fieldguide/PlotManagement";
import About from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AuthContext from "./auth/AuthContext";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import AdminRoute from "./auth/AdminRoute";
import ProtectedRoute from "./auth/ProtectedRoute";
import CropDetail from "./pages/CropDetail";
import PlotDetail from "./pages/PlotDetail";
import Christmas from "./pages/Christmas";
import Recipes from "./pages/Recipes";
import RecipeDetail from "./pages/RecipeDetail";

import "./App.css";

const App = () => {
  const { auth, logout, user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Handle window resize to detect mobile/desktop
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    if (!loading) {
      if (!auth?.user) {
        // Not logged in - redirect to login
        if (location.pathname === "/") {
          navigate("/login", { replace: true });
        }
      } else if (user?.preferred_garden && location.pathname === "/") {
        // Logged in with preferred garden - redirect to garden plan
        const currentYear = new Date().getFullYear();
        navigate(`/gardens/${user.preferred_garden.garden_uuid}/plan/${currentYear}`, { replace: true });
      } else if (location.pathname === "/") {
        // Logged in but no preferred garden - redirect to gardens list
        navigate("/gardens", { replace: true });
      }
    }
  }, [auth, user, loading, navigate, location.pathname]);

  return (
    <>
      <TopBar 
        onMenuToggle={toggleMobileMenu}
        isMenuOpen={isMobileMenuOpen}
        isMobile={isMobile}
        isSidebarCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => {
          const newState = !isSidebarCollapsed;
          setIsSidebarCollapsed(newState);
          localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
        }}
      />
      {/* Hide sidebar on desktop when on ManageGardenPlots page */}
      {!(location.pathname.includes("/plots") && !location.pathname.includes("/detail") && !isMobile) && (
        <Sidebar 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isMobile={isMobile}
          onMenuToggle={toggleMobileMenu}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => {
            const newState = !isSidebarCollapsed;
            setIsSidebarCollapsed(newState);
            localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
          }}
        />
      )}
      
      <Box className={`App-content ${isSidebarCollapsed && !isMobile ? 'sidebar-collapsed' : ''}`}>
        <Routes>
          <Route
            path="/"
            element={
              !loading && !auth?.user ? (
                <Navigate to="/login" replace />
              ) : !loading && auth?.user && user?.preferred_garden ? (
                <Navigate
                  to={`/gardens/${user.preferred_garden.garden_uuid}/plan/${new Date().getFullYear()}`}
                  replace
                />
              ) : !loading && auth?.user ? (
                <Navigate to="/gardens" replace />
              ) : (
                <CircularProgress />
              )
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={auth?.user ? <Profile /> : <Login />} />
          <Route
            path="/gardens"
            element={auth?.user ? <Gardens /> : <Login />}
          />
          <Route
            path="/gardens/:garden_uuid/:view?/:year?/:plot_group_uuid?"
            element={auth?.user ? <GardenCrops /> : <Login />}
          />
          <Route
            path="/gardens/:garden_uuid/plots/:plot_uuid/detail/:year"
            element={auth?.user ? <PlotDetail /> : <Login />}
          />
          <Route
            path="/gardens/:garden_uuid/plots"
            element={auth?.user ? <ManageGardenPlots /> : <Login />}
          />
          <Route
            path="/field-guide"
            element={
              <ProtectedRoute>
                <FieldGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="/field-guide/seasonal-planting"
            element={
              <ProtectedRoute>
                <SeasonalPlanting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/field-guide/crop-rotation"
            element={
              <ProtectedRoute>
                <CropRotation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/field-guide/soil-management"
            element={
              <ProtectedRoute>
                <SoilManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/field-guide/plot-management"
            element={
              <ProtectedRoute>
                <PlotManagement />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/crops" 
            element={
              <ProtectedRoute>
                <Crops />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/crops/:crop_uuid" 
            element={
              <ProtectedRoute>
                <CropDetail />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/recipes" 
            element={
              <ProtectedRoute>
                <Recipes />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/recipes/:creator" 
            element={
              <ProtectedRoute>
                <Recipes />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/recipes/:creator/:name" 
            element={
              <ProtectedRoute>
                <RecipeDetail />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/recipes/:recipe_uuid" 
            element={
              <ProtectedRoute>
                <RecipeDetail />
              </ProtectedRoute>
            }
          />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/christmas" element={<Christmas />} />
          <Route
            path="/management"
            element={
              <AdminRoute>
                <ManagementPage />
              </AdminRoute>
            }
          />
        </Routes>
      </Box>
    </>
  );
};

export default App;
